import React, { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { TabList, Tab } from '@fluentui/react-components';
import { ItemDetailsPanelBase } from './asset-panel-base/panel-base';
import { InfoPaneHeader } from './asset-panel-base/info-panel-header';
import { DeleteAssetDialog } from './asset-panel-base/delete-asset-dialog';
import { InfoPaneItemCopyUrl } from './basic-info/info-item-copy-url';
import { ItemInfoContentBase } from './basic-info/item-info-panel-content-base';
import { PerformanceContent } from './performance';

export const ImageDetailsPanel = ({
  isOpen,
  assetInfo,
  onDismiss,
  isReadonly,
  i18n,
  footerKeyDownHandler,
  getImageById,
  warningMsgProp, // for unit test
  logger,
  navigation,
  handleDeleteAsset,
  handleRenameAsset,
  gotoAdsPageWithAssetIdFilter,
  copyUrl,
  notificationApi,
  getGranularityEnum,
  loadPerfTimeSeriesData,
  perfMetricDateRange,
}) => {
  const { id: assetId, folderAssetType } = assetInfo || {};
  const [selectedTab, setSelectedTab] = useState('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [warningMsg, setWarningMsg] = useState(warningMsgProp || '');

  const onEditImage = useCallback(() => {
    logger.logTrace('Image Details: edit image', { assetId });
    onDismiss();
    navigation.gotoEditImagePage({ imageId: assetId });
  }, [assetId, logger, navigation, onDismiss]);

  const onDeleteImage = useCallback(() => {
    logger.logTrace('Image Details: delete image', { assetId });
    return handleDeleteAsset();
  }, [assetId, logger, handleDeleteAsset]);

  const onRenameImage = useCallback((newName) => {
    logger.logTrace('Image Details: rename image', { assetId });
    return handleRenameAsset(newName);
  }, [handleRenameAsset, assetId, logger]);

  const onCopyUrl = useCallback(() => {
    logger.logTrace('Image Details: url copied', { assetId });
    copyUrl(assetInfo.url);
  }, [logger, assetId, copyUrl, assetInfo.url]);

  const formatDate = useCallback(date => i18n.formatDate(date, { date: 'long' }), [i18n]);

  const imageInfoArr = useMemo(() => {
    const imageInfoList = [
      { name: i18n.getString(_TL_(('Image ID:'))), value: assetInfo.id },
      {
        name: i18n.getString(_TL_('Dimensions:')),
        value: i18n.getString(_TL_('{{width}} x {{height}}'), { width: assetInfo.width, height: assetInfo.height }),
      },
      { name: i18n.getString(_TL_('Created:')), value: formatDate(assetInfo.createdTime) },
      { name: i18n.getString(_TL_('Modified:')), value: formatDate(assetInfo.modifiedTime) },
      {
        CustomComponent: InfoPaneItemCopyUrl,
        CustomComponentOptions: {
          i18n,
          url: assetInfo.url,
          onCopy: onCopyUrl,
        },
        key: assetInfo.url,
      },
    ];

    return imageInfoList;
  }, [assetInfo.createdTime, assetInfo.height, assetInfo.id, assetInfo.modifiedTime, assetInfo.url, assetInfo.width, formatDate, i18n, onCopyUrl]);

  const onGotoAds = useCallback(() => {
    logger.logTrace('Image Details: go to Ads grid page', { assetId });
    return gotoAdsPageWithAssetIdFilter();
  }, [gotoAdsPageWithAssetIdFilter, assetId, logger]);

  useEffect(() => {
    if (getImageById && assetInfo && assetInfo.metadata) {
      const {
        id = '',
      } = assetInfo;
      getImageById({ imageId: id, useCache: false }).then((asset) => {
        if (asset && asset.isSceneKeyFrame) {
          setWarningMsg(i18n.getString(_TL_('Once deleted, the image will still serve in any video ads that currently use it and cannot be edited.')));
        } else {
          setWarningMsg('');
        }
      });
    } else if (warningMsg.length > 0) {
      setWarningMsg('');
    }
  }, [assetInfo, getImageById, i18n, setWarningMsg, warningMsg]);

  const dismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const onRenderHeader = useCallback(() => (
    <InfoPaneHeader
      isReadonly={isReadonly}
      i18n={i18n}
      title={i18n.getString(_TL_('Image details'))}
      onDelete={() => { setShowDeleteConfirm(true); }}
      onEdit={onEditImage}
      dismiss={dismiss}
    />
  ), [isReadonly, i18n, onEditImage, dismiss]);

  const closeDialog = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const onTabSelect = useCallback((event, data) => {
    setSelectedTab(data.value);
  }, []);

  return (
    <ItemDetailsPanelBase
      onRenderHeader={onRenderHeader}
      i18n={i18n}
      isOpen={isOpen}
      dismiss={dismiss}
      footerKeyDownHandler={footerKeyDownHandler}
    >
      <React.Fragment>
        <TabList selectedValue={selectedTab} onTabSelect={onTabSelect} style={{ marginTop: '2px' }}>
          <Tab className="performance-tab" value="performance">{i18n.getString(_TL_('Performance'))}</Tab>
          <Tab className="details-tab" value="details">{i18n.getString(_TL_('Details'))}</Tab>
        </TabList>
        {selectedTab === 'performance' && <PerformanceContent perfMetricDateRange={perfMetricDateRange} getGranularityEnum={getGranularityEnum} i18n={i18n} onGotoAds={onGotoAds} loadPerfTimeSeriesData={loadPerfTimeSeriesData} imageId={assetId} folderAssetType={folderAssetType} />}
        {selectedTab === 'details' && (
          <ItemInfoContentBase
            itemName={assetInfo.name}
            renameAsync={onRenameImage}
            promptNameInputLabel={i18n.getString(_TL_('Add image name'))}
            nameInputPlaceholderText={i18n.getString(_TL_('Enter image name'))}
            itemInfos={imageInfoArr}
            isReadonly={isReadonly}
            i18n={i18n}
            renameTitleText={i18n.getString(_TL_('Image name'))}
          />
        )}
        <DeleteAssetDialog
          onDeleteAsync={onDeleteImage}
          onDeleteSuccess={dismiss}
          confirmText={i18n.getString(_TL_('Are you sure you want to delete this image?'))}
          errorMessageForUsedAsset={i18n.getString(_TL_("You can't delete this image because it's being used by other campaigns, ads, or image extensions."))}
          genericErrorMessage={i18n.getString(_TL_('Your image couldn\'t be deleted. Please try again or contact support.'))}
          i18n={i18n}
          isDisplayed={showDeleteConfirm}
          warningMsg={warningMsg}
          closeDialog={closeDialog}
          notificationApi={notificationApi}
        />
      </React.Fragment>
    </ItemDetailsPanelBase>
  );
};

ImageDetailsPanel.propTypes = {
  assetInfo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    metadata: PropTypes.shape({}),
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
  isReadonly: PropTypes.bool,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  footerKeyDownHandler: PropTypes.func,
  getImageById: PropTypes.func.isRequired,
  warningMsgProp: PropTypes.string,
  loadPerfTimeSeriesData: PropTypes.func.isRequired,
};

ImageDetailsPanel.defaultProps = {
  isReadonly: false,
  footerKeyDownHandler: _.noop,
  warningMsgProp: '',
};
